import React, { useEffect, useState } from 'react';
import './DataPanel.css';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { clickLngLatAtom, isDesktopAtom, menuItemsAtom, selectedFeatureAtom, sidebarHeightAtom } from '../../atoms';
import type * as maplibreglType from 'maplibre-gl';
import { getFeatureArrData } from '../../lib/getFeatures';
import IconButton from '../button/IconButton';
import { useGetTileUrlsByType } from '../../lib/useGetTileUrls';
import classNames from 'classnames';
import { removeStrings } from '../../lib/stringUtils';
import { LAYER_GEOMETRY_TYPES } from '../../lib/smartCityStyle';
import { BsArrowsAngleContract, BsArrowsAngleExpand } from 'react-icons/bs';
import LocationInfo from '../locationInfo/LocationInfo';
import { formatTobus } from '../../lib/transitionFunc/formatTobus';
import { getValidFeatureKeys, omitIdProperty, uniqueFeaturesByKey } from '../../lib/propertiesFormatter';
import Loading from '../loading/Loading';
import AttributeValue from '../attributeValue/AttributeValue';
import { clearResult, highlightFeature } from '../../lib/objectHighlight';
import ReactGA from 'react-ga4';
import { sortDisasterReportsIfNeeded } from '../../lib/dataUtils';


type DataPanelPropsType = {
  remainingHeight?: number;
  mapClickEvent: maplibreglType.MapLayerMouseEvent | undefined;
  map: maplibreglType.Map | null;
  setMapClickEvent: (event: maplibreglType.MapLayerMouseEvent | undefined) => void;
};


const DataPanel: React.FC<DataPanelPropsType> = ({remainingHeight, mapClickEvent, map, setMapClickEvent}) => {

  const [selectedFeatures, setSelectedFeatures] = useAtom(selectedFeatureAtom);
  const [isFullHeight, setIsFullHeight] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [lngLat, setLngLat] = useAtom(clickLngLatAtom);

  // atoms
  const isDesktop = useAtomValue(isDesktopAtom);
  const menuItems = useAtomValue(menuItemsAtom);

  const setSidebarHeight = useSetAtom(sidebarHeightAtom);                                 // サイドバー開け閉め（スマホ）

  const dataPngUrls = useGetTileUrlsByType('datapng');
  const vectorTileUrls = useGetTileUrlsByType('vector');


  /* ******************
   * 閉じるボタンクリック処理
   * ******************/
  const onClickCloseBtn = () => {
    const features = selectedFeatures ? Object.values(selectedFeatures).flat() : undefined;
    clearResult(map, features);
    setSelectedFeatures(undefined);
    setIsFullHeight(false);
    setIsLoading(false);
    setMapClickEvent(undefined);
    setSidebarHeight('close');
  };

  /* ******************
   * もっと見るボタンクリック処理
   * ******************/
  const onClickMoreBtn = () => {
    setIsFullHeight(!isFullHeight);
  };

  /* ******************
   * データの取得
   * ******************/
  useEffect(() => {
    if (!map || !mapClickEvent) { return; }
    setIsLoading(true);
    (async () => {
      const features = await getFeatureArrData(map, mapClickEvent, menuItems, dataPngUrls, vectorTileUrls);

      if (!features) {
        setSelectedFeatures(undefined);
        setIsLoading(false);
        return;
      }
      
      const pointdata = features[Object.keys(features).find((key) => {
        return features[key].find((feature) => feature.geometry && 'type' in feature.geometry && feature.geometry.type === 'Point');
      })??''];

      // ポイントデータがったら、そのポイントの緯度経度をセットする
      if(pointdata) {
        const pointGeometry = pointdata[0].geometry as GeoJSON.Point;
        setLngLat([pointGeometry.coordinates[0], pointGeometry.coordinates[1]]);
      }
      
      /* ========== *
       *  TODO: opendata側で配列の持ち方実装した後、消す 
       * ========== */
      const updatedFeatures = formatTobus(features);

      // クリックした地物をハイライト
      if(updatedFeatures) {
        const features = Object.values(updatedFeatures).flat();
        highlightFeature(map, features);

        //【GoogleAnalytics】クリック地点の一番上のfeature クリックトラッキング
        ReactGA.event({
          category: "feature_click", 
          action: "click",
          label: updatedFeatures[Object.keys(updatedFeatures)[0]][0].layer.id
        });
      }
      
      setSelectedFeatures(updatedFeatures ? uniqueFeaturesByKey(updatedFeatures) : undefined);
      setIsLoading(false);
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapClickEvent]);

  // _id以外のプロパティを持つ地物のkeyのみを取得
  const displayFeatures = getValidFeatureKeys(selectedFeatures);

  if (!mapClickEvent) { return <></>; }

  return (
    <div className={
      classNames('data-panel', { 
        'high': isFullHeight && !isDesktop, 
        'low': (!selectedFeatures || Object.keys(selectedFeatures).length === 0) 
      })
      } 
      style={{ maxHeight: remainingHeight === 0 ? 'auto' : remainingHeight }}
    >
      <div className='data-panel-closeBtn'>
        <IconButton onClick={onClickCloseBtn} icon='close' color={isDesktop ? 'white' : 'black'} />
      </div>
      
      {/* 緯度経度・ボタン表示エリア */}
      <LocationInfo lngLat={lngLat}/>
      
      { isLoading ? 
        <Loading />
        :
        (displayFeatures && Object.keys(displayFeatures).length > 0 && selectedFeatures) 
        &&
          <>
            <div className='category-list'  style={isDesktop ? { maxHeight: remainingHeight ? remainingHeight - 108 : 'auto' } : {}}>
              {
                displayFeatures.map((key) => {
                  // Disasterreports（災害パトロール）の場合は日付降順でソート
                  const sortedFeatures = sortDisasterReportsIfNeeded(selectedFeatures[key]);

                  return (
                    <div key={key} className='category-container'>
                      <p className='category-name'>{removeStrings(key, [...Object.values(LAYER_GEOMETRY_TYPES), '-'])}</p>
                      <div className='data-items'>
                        {
                          sortedFeatures.map((feature, index) => {
                            const properties = feature.properties;
                            const myKey = `${key}-${feature.layer.id}-${index}`;

                            /** propertiesが空のオブジェクトの場合は表示しない */
                            if (Object.keys(properties).length === 0) { return null; }

                            return (
                              <div className='data-item' key={myKey}>
                                { // 属性情報
                                  Object.keys(omitIdProperty(properties)).map((itemKey, index) => {
                                    return (
                                      <div key={itemKey} className={classNames('data-item-row')}>
                                        <p className='key'>{itemKey}</p>
                                        <div className='value'>
                                          { <AttributeValue key={index} itemKey={itemKey} value={properties[itemKey]} />}
                                        </div>
                                      </div>
                                    );
                                  })
                                }
                              </div>
                            );
                          })
                        }
                      </div>
                    </div>
                  );
                })
              }
            </div>
            { /* もっと見るボタン */
              !isDesktop &&
              <button className="more-button" onClick={onClickMoreBtn}>
                {isFullHeight ? <BsArrowsAngleContract /> : <BsArrowsAngleExpand />}
                {isFullHeight ? '小さく表示' : '全画面表示'}
              </button>
            }
          </>
      }
    </div>
  );
};

export default DataPanel;